/**
 * Hook de estado del pickeo agrupado por lote.
 *
 * Envuelve la lógica pura de `pickeo-lotes.ts` con estado React y feedback
 * sonoro. Vive en la pantalla del comprobante (no en el modal) para que lo
 * pickeado se conserve al cerrar el modal.
 *
 * Persistencia: cada unidad pickeada se guarda en el back
 * (`POST …/pickeos`, stock a → r → p) y quitarla la cancela (`DELETE`). La
 * lectura se acepta al instante con la validación local y se confirma cuando
 * el back responde; si el back la rechaza (serie reservada por otro remito,
 * egresada…), se saca y se muestra el motivo. Al abrir el comprobante, el
 * estado se arma con los pickeos guardados (`guardados`, del detalle).
 *
 * `lectura` es segura ante llamadas consecutivas dentro del mismo tick (una
 * cola que drena varias lecturas seguidas): trabaja sobre un ref del estado y
 * no sobre el valor capturado en el último render. Prueba el texto principal
 * y las interpretaciones alternativas que trae `MetaLectura` (distribución de
 * teclado de la PC y del lector), y se queda con la que identifica la unidad.
 *
 * Diagnóstico: cada lectura (menos las vacías) se registra en el back
 * (`POST /api/pickeo-log/lectura`) con el texto crudo, el resultado, la traza
 * de teclas que arma `CampoLecturaGs1` y el entorno del navegador. Es
 * fire-and-forget: no condiciona el pickeo ni espera respuesta.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ComprobantePartida, PickeoRegistrado } from '@/api/contracts/comprobantes';
import {
  useCancelarPickeoMutation,
  useRegistrarLecturaPickeoMutation,
  useRegistrarPickeoMutation,
} from '@/api/queries';
import { toast } from '@/lib/notify';
import { playScanSound } from '@/lib/audio';
import type { MetaLectura } from './CampoLecturaGs1';
import { obtenerEntornoLector } from './entorno-lector';
import {
  agruparDetalle,
  confirmarPickeo,
  estadoInicialPickeo,
  fusionarPickeos,
  pickeosDesdeGuardados,
  procesarPickeoConAlternativas,
  quitarPickeo,
  rechazarPickeo,
  resumirPickeo,
  vistaLotes,
  type GrupoEsperado,
  type PickeoLotesState,
  type ResultadoPickeo,
  type ResumenPickeo,
  type VistaLote,
} from './pickeo-lotes';

export interface OpcionesPickeoLotes {
  /** Referencia ERP del despacho: identifica el remito al guardar cada unidad. */
  referencia?: string | null;
  /** Pickeos guardados en el back (vienen en el detalle del comprobante). */
  guardados?: readonly PickeoRegistrado[];
}

export interface UsePickeoLotesResult {
  grupos: GrupoEsperado[];
  state: PickeoLotesState;
  resumen: ResumenPickeo;
  vista: VistaLote[];
  /** Series ya pickeadas (para marcar filas en otras vistas). */
  seriesPickeadas: ReadonlySet<string>;
  /** Procesa una lectura del escáner o del teclado. Devuelve el resultado. */
  lectura: (raw: string, meta?: MetaLectura) => ResultadoPickeo;
  /** Cancela el pickeo de una unidad ya guardada. */
  quitar: (serie: string) => void;
  reset: () => void;
}

/** Tope del texto crudo que se manda al back (su schema acepta 4000). */
const MAX_TEXTO_LOG = 4000;

const SIN_GUARDADOS: readonly PickeoRegistrado[] = [];

export function usePickeoLotes(
  partidas: readonly ComprobantePartida[],
  { referencia = null, guardados = SIN_GUARDADOS }: OpcionesPickeoLotes = {},
): UsePickeoLotesResult {
  const grupos = useMemo(() => agruparDetalle(partidas), [partidas]);
  const [state, setState] = useState<PickeoLotesState>(estadoInicialPickeo);
  // Espejo sincrónico del estado: permite encadenar lecturas sin esperar al render.
  const stateRef = useRef(state);
  stateRef.current = state;
  const aplicar = useCallback((next: PickeoLotesState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  // Lo guardado en el back manda: cada vez que llega el detalle, se toma de
  // ahí y se conservan solo las lecturas que se están guardando.
  useEffect(() => {
    aplicar(fusionarPickeos(stateRef.current, pickeosDesdeGuardados(guardados, partidas)));
  }, [guardados, partidas, aplicar]);

  const resumen = useMemo(() => resumirPickeo(state, grupos), [state, grupos]);
  const vista = useMemo(() => vistaLotes(state, grupos), [state, grupos]);
  const seriesPickeadas = useMemo(() => new Set(state.pickeos.map((p) => p.serie)), [state]);

  const { mutate: registrarLectura } = useRegistrarLecturaPickeoMutation();
  const { mutate: registrarPickeo } = useRegistrarPickeoMutation();
  const { mutate: cancelarPickeo } = useCancelarPickeoMutation();
  // El detalle trae `pickeoTraza` (si el pickeo habilita "Trazar"): se vuelve
  // a pedir después de cada cambio guardado. Los pickeos que llegan se
  // reconcilian con los locales (ver el efecto de arriba).
  const queryClient = useQueryClient();
  const refrescarDetalle = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['comprobante'] });
  }, [queryClient]);

  /** Guarda en el back una unidad que la validación local aceptó. */
  const guardar = useCallback(
    (serie: string, grupoKey: string | undefined, gtinLeido: string | null) => {
      const grupo = grupos.find((g) => g.key === grupoKey);
      const gtin = gtinLeido ?? grupo?.gtin ?? null;
      const lote = grupo?.lote ?? null;
      if (!referencia || !gtin || !lote) {
        aplicar(
          rechazarPickeo(stateRef.current, serie, 'No se pudo guardar: falta el GTIN o el lote de la unidad.'),
        );
        playScanSound('error');
        return;
      }
      registrarPickeo(
        { referencia, gtin, lote, serie },
        {
          onSuccess: (guardado) => {
            aplicar(confirmarPickeo(stateRef.current, serie, guardado.stockId));
            refrescarDetalle();
          },
          onError: (err) => {
            aplicar(rechazarPickeo(stateRef.current, serie, err.message));
            playScanSound('error');
          },
        },
      );
    },
    [grupos, referencia, registrarPickeo, aplicar, refrescarDetalle],
  );

  const lectura = useCallback(
    (raw: string, meta?: MetaLectura) => {
      const textos = [raw, ...(meta?.alternativas ?? [])];
      const {
        state: next,
        resultado,
        textoUsado,
      } = procesarPickeoConAlternativas(stateRef.current, textos, grupos);
      aplicar(next);
      playScanSound(resultado.tipo === 'pickeada' ? 'success' : 'error');
      if (resultado.tipo === 'pickeada' && resultado.lectura?.serie) {
        guardar(resultado.lectura.serie, resultado.grupoKey, resultado.lectura.gtin);
      }
      if (resultado.tipo !== 'vacia') {
        void obtenerEntornoLector().then((entorno) => {
          registrarLectura({
            referencia,
            texto: textoUsado.slice(0, MAX_TEXTO_LOG),
            resultado: resultado.tipo,
            gtin: resultado.lectura?.gtin ?? null,
            lote: resultado.lectura?.lote ?? null,
            serie: resultado.lectura?.serie ?? null,
            duracionMs: meta?.duracionMs ?? null,
            teclas: meta?.teclas ?? null,
            entorno,
          });
        });
      }
      return resultado;
    },
    [grupos, referencia, registrarLectura, aplicar, guardar],
  );

  const quitar = useCallback(
    (serie: string) => {
      const pickeo = stateRef.current.pickeos.find((p) => p.serie === serie);
      if (!pickeo?.stockId || !referencia) return; // sin guardar todavía: no hay qué cancelar
      aplicar(quitarPickeo(stateRef.current, serie));
      cancelarPickeo(
        { referencia, stockId: pickeo.stockId },
        {
          onSuccess: refrescarDetalle,
          onError: (err) => {
            // No se canceló en el back: la unidad sigue pickeada.
            aplicar({ ...stateRef.current, pickeos: [...stateRef.current.pickeos, pickeo] });
            toast.error(`No se pudo quitar la serie ${serie}: ${err.message}`);
          },
        },
      );
    },
    [referencia, cancelarPickeo, aplicar, refrescarDetalle],
  );

  /** Vacía solo el estado local (no cancela nada en el back). */
  const reset = useCallback(() => {
    aplicar(estadoInicialPickeo());
  }, [aplicar]);

  return { grupos, state, resumen, vista, seriesPickeadas, lectura, quitar, reset };
}
